<template>
    <div>
        <h1 class="page-header">{{ $t('str.form.title.timeline.free.patrols') }}</h1>
        <hr />
        <div class="row">
            <div v-if="isSuperAdminMaster" class="col-md-4 mb-3">
                <label class="form-label" for="accountField">{{ $t('str.register.guard.groups.account.field') }}</label>
                <select v-model="filters.account" @change="changeAccount" class="form-select" id="accountField">
                    <option value="">{{ $t('str.register.select.placeholder') }}</option>
                    <option v-for="account in accounts" :value="account._id" :key="account._id">
                        {{ account.name }}
                    </option>
                </select>
                <div class="invalid-feedback">{{ $t('str.register.guard.groups.account.required') }}</div>
            </div>
            <div v-if="role === 'SUPER_ADMIN_MASTER' || role === 'ADMIN' || role === 'MANAGER'" class="col-md-4 mb-3">
                <label class="form-label" for="clientField">{{ $t('str.register.guard.groups.client.field') }}</label>
                <select v-model="filters.client" @change="changeClient" class="form-select" id="clientField">
                    <option value="">{{ $t('str.register.select.placeholder') }}</option>
                    <option v-for="client in clients" :value="client._id" :key="client._id">
                        {{ client.name }}
                    </option>
                </select>
                <div class="invalid-feedback">{{ $t('str.register.guard.groups.client.required') }}</div>
            </div>
            <div class="col-md-4 mb-3">
                <label class="form-label" for="siteField">{{ $t('str.register.guard.groups.site.field') }}</label>
                <select v-model="filters.site" @change="changeSite" class="form-select" id="siteField">
                    <option value="">{{ $t('str.register.select.placeholder') }}</option>
                    <option v-for="site in sites" :value="site._id" :key="site._id">
                        {{ site.name }}
                    </option>
                </select>
                <div class="invalid-feedback">{{ $t('str.register.guard.groups.site.required') }}</div>
            </div>
            <div class="col-md-4 mb-3">
                <label class="form-label" for="statusField">{{ $t('str.range.date.field') }}</label>
                <date-range-picker
                    ref="picker"
                    class="form-select btn btn-dark me-2"
                    :opens="dateRange.opens"
                    :locale-data="dateRange.sampleLocaleData"
                    :singleDatePicker="dateRange.singleDatePicker"
                    :timePicker="dateRange.timePicker"
                    :timePicker24Hour="dateRange.timePicker24Hour"
                    :showWeekNumbers="dateRange.showWeekNumbers"
                    :showDropdowns="dateRange.showDropdowns"
                    :autoApply="dateRange.autoApply"
                        v-model="dateRange.range"
                        :ranges="dateRanges"
                    :max-date="dateRange.maxDate"
                    @update="updateValues"
                    :linkedCalendars="dateRange.linkedCalendars"
                    :date-range="dateRange"
                    :locale="dateRange.locale"
                >
                    <template v-slot:input="filters">
                        <i class="fa fa-calendar fa-fw text-white text-opacity-50 ms-n1"></i>
                        {{ formatDate(filters.startDate).split(' - ')[0] }} - {{ formatDate(filters.endDate).split(' - ')[0] }}
                        <b class="caret ms-1 opacity-5"></b>
                    </template>
                </date-range-picker>
            </div>

            <div class="col-md-4 mb-3">
                <label class="form-label" for="statusField">{{ $t('str.register.status.field') }}</label>
                <select v-model="filters.status" @change="filterFromFirstPage" class="form-select" id="statusField">
                    <option value="">{{ $t('str.register.select.all.placeholder') }}</option>
                    <option value="IN_PROGRESS">{{ $t('str.status.in.progress') }}</option>
                    <option value="FINISHED">{{ $t('str.event.status.done') }}</option>
                    <option value="CANCELLED">{{ $t('str.status.cancelled') }}</option>
                </select>
            </div>
             <div class="col-md-2 text-center align-self-end mb-3">
                <button @click="filterFromFirstPage" type="submit" class="w-100 btn btn-primary is-loading">
                    <i v-if="isLoading" class="fas fa-spinner fa-pulse"></i>
                    {{ $t('str.btn.search') }}
                </button>
            </div>
        </div>

        <div>
            <vue-good-table
                :columns="columns"
                :rows="items"
                :totalRows="totalItems"
                :search-options="{ enabled: true, placeholder: $t('str.table.search.in.this.table') }"
                :pagination-options="paginationOptions"
                @on-page-change="onPageChange"
                @on-per-page-change="onPerPageChange"
                @on-row-click="selectItem"
            >
                <div slot="emptystate" class="vgt-center-align vgt-text-disabled">
                    <i v-if="isLoading" class="fas fa-spinner fa-spin" />
                    <span v-if="!isLoading && items?.length === 0">{{ $t('str.table.subtitle.no.data') }}</span>
                </div>

                <template slot="table-row" slot-scope="props">
                    <span v-if="props.column.field === 'vigilant'"> {{ props.formattedRow[props.column.field]?.firstName }} {{ props.formattedRow[props.column.field]?.lastName }} </span>

                    <span v-else-if="props.column.field === 'startDate' || props.column.field === 'endDate'">
                        {{ formatDate(props.formattedRow[props.column.field]) }}
                    </span>
                    <span v-else-if="props.column.field === 'status'">
                        <span
                            class="badge"
                            v-bind:class="
                                props.formattedRow[props.column.field] === 'ACTIVE'
                                    ? 'bg-blue'
                                    : props.formattedRow[props.column.field] === 'IN_PROGRESS'
                                    ? 'bg-success'
                                    : props.formattedRow[props.column.field] === 'FINISHED'
                                    ? 'bg-green'
                                    : props.formattedRow[props.column.field] === 'CANCELLED'
                                    ? 'bg-indigo'
                                    : 'bg-dark'
                            "
                        >
                            {{ $t(getStatusName(props.formattedRow[props.column.field])) }}
                        </span>
                    </span>

                    <span v-else>
                        {{ props.formattedRow[props.column.field] }}
                    </span>
                </template>
            </vue-good-table>
        </div>
        <notifications group="top-right" position="top right" :speed="1000" />
        <InfoItemModal :selectedItem="selectedItem" :isSuperAdminMaster="isSuperAdminMaster" />
    </div>
</template>

<script>
import moment from 'moment-timezone'
import Controller from './CrtTimelineFreePatrol.vue'
import InfoItemModal from './InfoItemModal/InfoItemModal.vue'
import Common from '../../../common/Common.vue'
import Vue from 'vue'
import { createTimelineDateRange } from '../../../utils/timeline'
Vue.prototype.$registerEvent = new Vue()
export default {
    components: {
        InfoItemModal,
    },
    props: ['filterParams'],
    data() {
        const accountTimezone = Common.getAccountTimezone()
        return {
            accounts: [],
            clients: [],
            sites: [],
            errors: [],
            items: [],
            columns: [],
            isLoading: false,
            dateRange: createTimelineDateRange(accountTimezone),
            role: '',

            totalItems: 0,
            currentPage: 1,
            paginationOptions: {
                perPage: 10,
                enabled: true,
            },

            filters: {
                account: '',
                client: '',
                site: '',
                status: '',
                startDate: moment.tz(accountTimezone),
                endDate: moment.tz(accountTimezone),
                skip: 1,
                limit: 10,
            },
            selectedItem: null,
                userLocale: 'pt-br',
                maxDate: moment.tz(accountTimezone).endOf('day').toDate(),
                dateRanges: {
                    [this.$t('str.date.today')]: [moment.tz(accountTimezone).toDate(), moment.tz(accountTimezone).toDate()],
                    [this.$t('str.date.yesterday')]: [moment.tz(accountTimezone).subtract(1, 'days').toDate(), moment.tz(accountTimezone).subtract(1, 'days').toDate()],
                    [this.$t('str.date.this.month')]: [moment.tz(accountTimezone).startOf('month').toDate(), moment.tz(accountTimezone).toDate()],
                    [this.$t('str.date.last.month')]: [moment.tz(accountTimezone).subtract(1, 'month').startOf('month').toDate(), moment.tz(accountTimezone).subtract(1, 'month').endOf('month').toDate()],
                },
            isSuperAdminMaster: false,
        }
    },
    mounted() {
        this.applyRouteFilters()
    },
    methods: Controller.methods,
    created() {
        Controller.init(this)

        const state = this
        state.$registerEvent.$on('changeLanguage', function () {
            state.initTable()
            state.initRangeDate()
            state.userLocale = state.$i18n.locale === 'pt' ? 'pt-br' : 'en'
            try {
                if (state.$refs && state.$refs.picker && state.$refs.picker.$data) state.$refs.picker.$data.ranges = state.dateRanges
            } catch (e) {
                // ignore
            }
        })
    },
}
</script>
<style lang="scss" scoped>
.vue-daterange-picker.btn {
    padding: 2px !important;
    display: flex !important;
    background-color: var(--app-component-bg) !important;
    text-align-last: start !important;
}
.caret {
    display: none !important;
}
</style>
